// src/hooks/useFirebase.js
import { useState, useEffect, useMemo } from "react";
import {
  collection, doc, addDoc, updateDoc, setDoc, deleteDoc,
  onSnapshot, orderBy, query, serverTimestamp,
} from "firebase/firestore";
import {
  ref, uploadBytesResumable, getDownloadURL, deleteObject,
} from "firebase/storage";
import { db, storage } from "../firebase";

// ── Construction Types ────────────────────────────────────────
export function useTypes() {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, "types"), orderBy("createdAt", "asc"));
    const unsub = onSnapshot(q, (snap) => {
      setTypes(snap.docs.map((d) => ({ id: d.id, ...d.data(), projects: [] })));
      setLoading(false);
    });
    return unsub;
  }, []);

  const addType = (name, icon = "◈") =>
    addDoc(collection(db, "types"), { name, icon, createdAt: serverTimestamp() });

  const deleteType = async (typeId) => {
    // Projects and photos are cleaned up via cascade in deleteProject
    const snap = await import("firebase/firestore").then(({ getDocs, collection }) =>
      getDocs(collection(db, "types", typeId, "projects"))
    );
    for (const d of snap.docs) await deleteProject(typeId, d.id, d.data().photos || []);
    await deleteDoc(doc(db, "types", typeId));
  };

  return { types, loading, addType, deleteType };
}

// ── Projects (subcollection under each type) ──────────────────
export function useProjects(typeId) {
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    if (!typeId) return;
    const q = query(
      collection(db, "types", typeId, "projects"),
      orderBy("createdAt", "asc")
    );
    const unsub = onSnapshot(q, (snap) => {
      setProjects(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [typeId]);

  const addProject = (typeId, { name, year, desc }) =>
    addDoc(collection(db, "types", typeId, "projects"), {
      name, year, desc, photos: [], createdAt: serverTimestamp(),
    });

  return { projects, addProject };
}

// ── Photo upload with progress ────────────────────────────────
export function usePhotoUpload() {
  const [progress, setProgress] = useState({});   // { filename: 0-100 }
  const [uploading, setUploading] = useState(false);

  const uploadPhotos = async (typeId, projectId, files, existingPhotos) => {
    setUploading(true);
    const uploaded = [];

    for (const file of Array.from(files)) {
      const path = `photos/${typeId}/${projectId}/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, path);
      const task = uploadBytesResumable(storageRef, file);

      await new Promise((resolve, reject) => {
        task.on(
          "state_changed",
          (snap) => {
            const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
            setProgress((p) => ({ ...p, [file.name]: pct }));
          },
          reject,
          async () => {
            const url = await getDownloadURL(task.snapshot.ref);
            uploaded.push({ url, name: file.name, storagePath: path });
            resolve();
          }
        );
      });
    }

    // Persist URLs to Firestore
    const projectRef = doc(db, "types", typeId, "projects", projectId);
    await setDoc(projectRef, { photos: [...existingPhotos, ...uploaded] }, { merge: true });

    setUploading(false);
    setProgress({});
    return uploaded;
  };

  return { uploadPhotos, uploading, progress };
}

// ── Delete a single photo ─────────────────────────────────────
export async function deletePhoto(typeId, projectId, photo, existingPhotos) {
  if (photo.storagePath) {
    try { await deleteObject(ref(storage, photo.storagePath)); } catch (_) {}
  }
  const remaining = existingPhotos.filter((p) => p.url !== photo.url);
  await updateDoc(doc(db, "types", typeId, "projects", projectId), { photos: remaining });
}

// ── Delete a project + its photos ────────────────────────────
export async function deleteProject(typeId, projectId, photos = []) {
  for (const ph of photos) {
    if (ph.storagePath) {
      try { await deleteObject(ref(storage, ph.storagePath)); } catch (_) {}
    }
  }
  await deleteDoc(doc(db, "types", typeId, "projects", projectId));
}

// ── Move a project to a different construction type ───────────
export async function moveProjectType(fromTypeId, toTypeId, projectId, project) {
  if (fromTypeId === toTypeId) return;
  const { id, ...data } = project;
  await setDoc(doc(db, "types", toTypeId, "projects", projectId), data);
  await deleteDoc(doc(db, "types", fromTypeId, "projects", projectId));
}

// ── All projects flat (for the portfolio grid) ────────────────
export function useAllProjectsFlat(types) {
  const [byType, setByType] = useState({});
  const typeKey = useMemo(() => types.map(t => t.id).join(","), [types]);

  useEffect(() => {
    if (!types.length) return;
    const unsubs = types.map(type => {
      const q = query(collection(db, "types", type.id, "projects"), orderBy("createdAt", "asc"));
      return onSnapshot(q, snap => {
        setByType(prev => ({
          ...prev,
          [type.id]: snap.docs.map(d => ({ id: d.id, typeId: type.id, typeName: type.name, ...d.data() })),
        }));
      });
    });
    return () => unsubs.forEach(u => u());
  }, [typeKey]); // eslint-disable-line

  return useMemo(
    () => types.flatMap(t => byType[t.id] || []),
    [types, byType]
  );
}
